import React from 'react';
import {
  Container,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import AuthorsSidebar from '../../components/AuthorsSidebar';
import {
  ExpandMore as ExpandMoreIcon,
  QuestionAnswer,
  Support,
  Feedback,
  Help,
} from '@mui/icons-material';

const FAQ: React.FC = () => {
  const faqCategories = [
    {
      category: 'Общие вопросы',
      questions: [
        {
          question: 'Как подать статью в журнал?',
          answer: 'Для подачи статьи необходимо зарегистрироваться на сайте, войти в личный кабинет и заполнить форму подачи статьи. Подробная инструкция доступна в разделе "Как опубликовать статью".',
        },
        {
          question: 'Сколько времени занимает рецензирование?',
          answer: 'Средний срок рецензирования составляет 4-6 недель. В случае необходимости дополнительных правок срок может быть продлен до 8 недель.',
        },
        {
          question: 'Можно ли подать статью на английском языке?',
          answer: 'Да, мы принимаем статьи на русском и английском языках. Для статей на английском языке требуется аннотация на русском языке.',
        },
        {
          question: 'Какие форматы файлов принимаются?',
          answer: 'Основной формат - PDF. Дополнительно можно приложить исходные файлы в формате DOC/DOCX или LaTeX.',
        },
      ],
    },
    {
      category: 'Технические вопросы',
      questions: [
        {
          question: 'Как отследить статус моей статьи?',
          answer: 'Статус статьи можно отслеживать в личном кабинете автора. Уведомления о смене статуса приходят на указанный email.',
        },
        {
          question: 'Можно ли отозвать поданную статью?',
          answer: 'Да, статья может быть отозвана автором до начала рецензирования. После начала рецензирования отзыв возможен только по согласованию с редакцией.',
        },
        {
          question: 'Как исправить ошибки в поданной статье?',
          answer: 'После подачи статьи изменения возможны только до начала рецензирования. Для внесения изменений обратитесь в редакцию.',
        },
        {
          question: 'Что делать, если забыл пароль?',
          answer: 'Используйте функцию "Забыли пароль?" на странице входа. На ваш email будет отправлена ссылка для восстановления пароля.',
        },
      ],
    },
    {
      category: 'Оформление и требования',
      questions: [
        {
          question: 'Какие требования к оформлению статьи?',
          answer: 'Статья должна быть оформлена в соответствии с требованиями: шрифт Times New Roman, 14 пт, междустрочный интервал 1.5, поля 2 см, объем от 5 до 20 страниц.',
        },
        {
          question: 'Нужно ли указывать все соавторов?',
          answer: 'Да, все лица, внесшие существенный вклад в исследование, должны быть указаны как соавторы. Все соавторы должны согласиться с публикацией.',
        },
        {
          question: 'Как правильно оформить список литературы?',
          answer: 'Список литературы должен быть оформлен по ГОСТ Р 7.0.5-2008. Все источники должны быть актуальными и авторитетными.',
        },
        {
          question: 'Можно ли использовать изображения из интернета?',
          answer: 'Использование изображений возможно только при наличии разрешения правообладателя или при использовании изображений с открытой лицензией (Creative Commons).',
        },
      ],
    },
    {
      category: 'Публикация и доступ',
      questions: [
        {
          question: 'Когда статья будет опубликована?',
          answer: 'После принятия статьи к публикации она публикуется в течение 1-2 месяцев. Точные сроки зависят от загруженности редакции.',
        },
        {
          question: 'Будет ли статья доступна в открытом доступе?',
          answer: 'Да, все статьи журнала публикуются в открытом доступе под лицензией Creative Commons Attribution (CC BY).',
        },
        {
          question: 'Получу ли я печатный экземпляр журнала?',
          answer: 'Печатные экземпляры предоставляются по запросу в ограниченном количестве. Основной формат публикации - электронный.',
        },
        {
          question: 'Можно ли опубликовать статью в другом журнале?',
          answer: 'После публикации в нашем журнале повторная публикация в других изданиях не допускается без письменного разрешения редакции.',
        },
      ],
    },
  ];

  const supportContacts = [
    {
      type: 'Техническая поддержка',
      contact: 'support@tyumen-science.ru',
      description: 'Проблемы с системой, доступом, техническими вопросами',
    },
    {
      type: 'Редакция',
      contact: 'tyumen-science@yandex.ru',
      description: 'Вопросы по содержанию, рецензированию, публикации',
    },
    {
      type: 'Главный редактор',
      contact: 'editor@tyumen-science.ru',
      description: 'Серьезные вопросы, жалобы, предложения',
    },
  ];

  return (
    <AuthorsSidebar>
      <Container sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Вопросы-ответы
      </Typography>
      
      <Typography variant="body1" paragraph>
        Здесь собраны ответы на наиболее часто задаваемые вопросы авторов. 
        Если вы не нашли ответ на свой вопрос, обратитесь в редакцию.
      </Typography>

      <Alert severity="info" sx={{ mb: 4 }}>
        Для получения быстрого ответа рекомендуем сначала ознакомиться с данным разделом, 
        а затем обращаться в редакцию с конкретными вопросами.
      </Alert>

      {faqCategories.map((category, categoryIndex) => (
        <Paper key={categoryIndex} sx={{ p: 3, mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <QuestionAnswer />
            {category.category}
          </Typography>
          
          {category.questions.map((faq, questionIndex) => (
            <Accordion key={questionIndex} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>{faq.answer}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>
      ))}

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Support />
          Техническая поддержка
        </Typography>
        <Typography variant="body1" paragraph>
          Если у вас возникли технические проблемы или вопросы, не описанные в FAQ, 
          обратитесь в соответствующую службу поддержки.
        </Typography>
        
        <List>
          {supportContacts.map((contact, index) => (
            <ListItem key={index}>
              <ListItemIcon>
                <Help color="primary" />
              </ListItemIcon>
              <ListItemText
                primary={contact.type}
                secondary={`${contact.contact} - ${contact.description}`}
              />
            </ListItem>
          ))}
        </List>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Feedback />
          Обратная связь
        </Typography>
        <Typography variant="body1" paragraph>
          Ваши вопросы и предложения помогают нам улучшать работу журнала. 
          Мы ценим ваше мнение и готовы к диалогу.
        </Typography>
        
        <Box sx={{ mt: 2, p: 2, backgroundColor: 'primary.light', borderRadius: 1 }}>
          <Typography variant="body2" color="primary.contrastText">
            <strong>Совет:</strong> При обращении в редакцию указывайте номер статьи (если есть), 
            ваше имя и конкретный вопрос. Это поможет нам быстрее решить вашу проблему.
          </Typography>
        </Box>
      </Paper>
      </Container>
    </AuthorsSidebar>
  );
};

export default FAQ;
